import { logDebugCacheEvent, logGenericEvent, } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { logError as otelLogError } from '@office-start/telemetry-service';
var unknownError = 'UnknownError';
var handler;
/**
 * Gets a singleton error handler object that handles global errors by either
 * swallowing them or logging them.
 *
 * @deprecated This function should no longer be used. Specific errors should be logged via
 * the `@office-start/telemetry-service`.
 */
export function getErrorHandler() {
    if (!handler) {
        var loggedErrors_1 = new Map();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var setErrorId_1 = function (error, errorId) {
            if (error && typeof error === 'object' && errorId) {
                error.id = errorId;
            }
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var shouldSwallow_1 = function (error) {
            // If handler called explicitly then allow error representing code failure to pass through.
            // Use presence of 'stack' property as a proxy for code error unless it is error from promise rejection which is handled below.
            if (error && error.stack && !error.promise) {
                return false;
            }
            // Rejection from promises that do not have explicit .catch() and error callback will be wrapped and passed through to this error handler.
            // We want to swallow the errors that simply indicate that promise was rejected. This can happen in variety of ways because reject accepts
            // 'any' as parameter. So assumption that we make here is that if object passed to rejection does not have 'stack' property then it is
            // safe to swallow.
            // However there is significant number of cases where rejection id done by doing 'throw new Error()'. We are seeing it in our codebase
            // especially in conjunction with async/ await pattern. When that happens generated error will have its rejection property set to Error
            // with stack pointing to the place where error was thrown. In order to swallow that kind of rejection we make an assertion that we will
            // swallow errors that have error.name === 'Error' (i.e. objects instantiated using new Error()). It is safe assumption because the errors
            // that we want to log are code errors and those tend to be specialized: TypeError, SyntaxError, etc.
            if (error && error.promise && error.rejection) {
                var rejection = error.rejection;
                if (rejection.stack && rejection.name !== 'Error') {
                    return false;
                }
            }
            return true;
        };
        var determineOccurrence_1 = function (errorMessage) {
            return 1 + (loggedErrors_1.get(errorMessage) || 0);
        };
        var shouldLogBasedOnOccurrence_1 = function (occurrence) {
            var occurrenceLength = occurrence.toString().length;
            var threshold = Math.pow(10, occurrenceLength - 1);
            return threshold < 2 ? true : occurrence % threshold === 0;
        };
        var safeLogErrorData_1 = function (errorInfo, debugInfo, category, logDebugCache) {
            var _a;
            if (logDebugCache === void 0) { logDebugCache = false; }
            // Log error properties; if this code fails then abandon logging
            try {
                // Keep track of all logged info to prevent over-logging of exceptions that happen in cycle.
                // This is done to ensure that we do not overwhelm system with exceptions that happen frequently.
                // Basically what happens here is that we reduce the number of logged events that are instances of the same exception.
                // All occurrences from 1-9 are logged individually; from 10-100 only when we hit occurrence 10, 20, 30, 40, ... 90;
                // once we hit 100s we will only log 100, 200, ... 900 occurrence; anything beyond 1000 will get logged on 1000, 2000, 3000, ... 9000
                // and so on and so forth for next power of 10.
                var occurrence = determineOccurrence_1(errorInfo);
                loggedErrors_1.set(errorInfo, occurrence);
                if (shouldLogBasedOnOccurrence_1(occurrence)) {
                    var params = (_a = {},
                        _a["Error" /* InstrumentationParam.Error */] = errorInfo,
                        _a["DebugInfo" /* InstrumentationParam.DebugInfo */] = {
                            value: debugInfo,
                            kind: 2 /* PiiKind.GenericData */,
                        },
                        _a["ItemCategory" /* InstrumentationParam.ItemCategory */] = category,
                        _a["ItemCount" /* InstrumentationParam.ItemCount */] = occurrence.toString(),
                        _a);
                    logGenericEvent(KnownInstrumentationEvents.UnhandledException, params);
                    if (logDebugCache) {
                        params["DebugInfo" /* InstrumentationParam.DebugInfo */] = debugInfo;
                        logDebugCacheEvent(KnownInstrumentationEvents.UnhandledException, params);
                    }
                    // TODO: Remove above aria logging after OTEL migration
                    otelLogError({
                        eventName: 'Unhandled_Exception',
                        errorType: 'Unhandled_Exception',
                        errorCategory: category,
                        occurrenceCount: occurrence,
                        message: errorInfo,
                        debugInfo: debugInfo,
                    });
                }
            }
            catch (e) {
                // Delegate to the default handler but bypass our implementation of handleError() to prevent infinite loop
                console.log(e);
            }
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var logError_1 = function (error, category) {
            // Errors can be reported in variety of ways on different browsers. In addition to category we will compose two
            // additional string values that will be logged as Error and DebugInfo. Note that Error will be logged in clear
            // text and DebugInfo will be logged as PiiString so take special care with data that goes into Error log property.
            var errorInfo = '';
            var debugInfo = '';
            error = error || new Error(unknownError);
            var logDebugCache = false;
            try {
                // Extract filename and location of the error in the script
                var filename = error.filename;
                if (filename) {
                    // PII data maybe present in file path so ensure to keep only file name
                    filename = filename.replace(/^.*[\\\/]/, '');
                    errorInfo = "Error location: ".concat(filename, ", line: ").concat(error.lineno, ", col: ").concat(error.colno);
                }
                else if (error.id) {
                    // Identifiers can be found in error-id.ts and used to search for occurrence of error
                    errorInfo = "Error id: ".concat(error.id);
                }
                else {
                    errorInfo = 'Error location: unknown';
                    logDebugCache = true;
                }
                // Determine error object containing original error. Modern browsers pass ErrorEvent object which contains error
                // object in separate property
                var errorObject = error.error || error;
                // Sometimes passed error can be a wrapper around another error
                if (errorObject.originalError) {
                    errorInfo += "\nHandled error is a wrapper with message: ".concat(errorObject.message);
                    debugInfo = "Wrapper call stack: ".concat(errorObject.stack || 'Call stack trace is not available');
                    errorObject = errorObject.originalError;
                    debugInfo += "\nOriginal call stack: ".concat(errorObject.stack || 'Call stack trace is not available');
                }
                else {
                    debugInfo = "Call stack: ".concat(errorObject.stack || 'Call stack trace is not available');
                }
                // Append error message to logged error info
                if (errorObject.message) {
                    errorInfo += "\nError message: ".concat(errorObject.message);
                }
            }
            catch (e) {
                errorInfo = "Error caught inside error logger: ".concat((e && e.message) || unknownError);
                debugInfo = "Call stack: ".concat((e && e.stack) || 'Call stack trace is not available');
                category = "React" /* ErrorCategories.React */;
                // Log to console instead of our handler to prevent infinite loop
                console.log(e);
            }
            finally {
                safeLogErrorData_1(errorInfo, debugInfo, category, logDebugCache);
                // Log to console as last resort
                console.log(error);
            }
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        var handleError_1 = function (error, errorId) {
            if (!shouldSwallow_1(error)) {
                setErrorId_1(error, errorId);
                // TODO :Should default category come from context?
                logError_1(error, "React" /* ErrorCategories.React */);
            }
        };
        var mutateGlobalWindowErrorHandler = function () {
            var origErrorHandler = window.onerror;
            window.onerror = function (message, filename, lineno, colno, error) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                var errorToLog = null;
                // Modern browsers pass message as ErrorEvent so prefer to use it for logging; otherwise use passed error object.
                // If error is not available then construct error based on message.
                if (typeof message === 'object' &&
                    message.type &&
                    message.type.toLowerCase() === 'error') {
                    errorToLog = message;
                }
                else if (error) {
                    errorToLog = error;
                    errorToLog.message = error.message || message;
                    Object.assign &&
                        Object.assign(errorToLog, {
                            filename: filename,
                            lineno: lineno,
                            colno: colno,
                        });
                }
                else if (typeof message === 'string') {
                    errorToLog = new Error(message || unknownError);
                    Object.assign &&
                        Object.assign(errorToLog, {
                            filename: filename,
                            lineno: lineno,
                            colno: colno,
                        });
                }
                else {
                    errorToLog = new Error(unknownError);
                    Object.assign &&
                        Object.assign(errorToLog, {
                            filename: filename,
                            lineno: lineno,
                            colno: colno,
                        });
                }
                logError_1(errorToLog, "Global" /* ErrorCategories.Global */);
                // Call the original onerror handler on set of arguments that was passed to the override
                if (origErrorHandler) {
                    try {
                        origErrorHandler.apply(window, [
                            message,
                            filename,
                            lineno,
                            colno,
                            error,
                        ]);
                    }
                    catch (e) {
                        handleError_1(e);
                    }
                }
            };
        };
        mutateGlobalWindowErrorHandler();
        handler = {
            handleError: handleError_1,
            handleCacheObservableError: function (error, cacheErrorId, networkErrorId) {
                // Error returned for Observable which has both cache and network data is a wrapper around another error(s)
                // Pass caught errors to global handler to ensure that they are properly logged in case of code error
                if (error) {
                    if (error.networkError) {
                        handleError_1(error.networkError, networkErrorId);
                    }
                    if (error.cacheError) {
                        handleError_1(error.cacheError, cacheErrorId);
                    }
                }
            },
        };
    }
    return handler;
}
//# sourceMappingURL=getErrorHandler.js.map